import { Plus, Trash2 } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import Modal from "../../../../components/modal/Modal";
import { AgGridReact } from "ag-grid-react";
import type { RoundType } from "../../../../types/api";
import { useRoundTypesStore } from "../../../../store/masterDataStore";
import { toast } from "react-toastify";
import images from "../../../../assets/images";

const RoundTypeList: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [edited, setEdited] = useState<RoundType | null>(null);
  const [rows, setRows] = useState<RoundType[]>([]);
  const [formData, setFormData] = useState<{ roundType: string }>({
    roundType: "",
  });

  const {
    roundTypes,
    fetchAllRoundTypes,
    createRoundType,
    updateRoundType,
    deleteRoundType,
  } = useRoundTypesStore();

  const handleOpenModal = () => {
    setIsOpen(true);
    setEdited(null);
    setFormData({ roundType: "" });
  };

  useEffect(() => {
    fetchAllRoundTypes();
  }, []);

  useEffect(() => {
    setRows(roundTypes);
  }, [roundTypes]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.roundType?.trim()) {
      toast.error("Round Type is required");
      return;
    }

    try {
      if (edited && edited._id) {
        const success = await updateRoundType(formData.roundType, edited._id);
        if (success) {
          toast.success("Round Type updated successfully");
        } else {
          toast.error("Failed to update Round Type");
          return;
        }
      } else {
        const success = await createRoundType(formData.roundType);
        if (success) {
          toast.success("Round Type created successfully");
        } else {
          toast.error("Failed to create Round Type");
          return;
        }
      }

      setIsOpen(false);
      setEdited(null);
      setFormData({ roundType: "" });
      await fetchAllRoundTypes();
    } catch (error) {
      console.error("Error saving round type:", error);
      toast.error("Error saving round type");
    }
  };

  const handleEdit = async (row: RoundType & { _id: string }) => {
    setEdited(row);
    setFormData({
      roundType: row.roundType,
    });
    setIsOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this round type?")) {
      try {
        const success = await deleteRoundType(id);
        if (success) {
          toast.success("Round Type deleted successfully");
          await fetchAllRoundTypes();
        } else {
          toast.error("Failed to delete Round Type");
        }
      } catch (error) {
        console.error("Error deleting round type", error);
        toast.error("Error deleting round type");
      }
    }
  };

  const [columnDefs] = useState<any>([
    {
      field: "roundType",
      headerName: "Round Type",
      minWidth: 20,
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      filter: false,
      cellStyle: {
        padding: "10px 5px 0 10px",
        textAlign: "left",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      },
      tooltipField: "roundType",
    },
    {
      field: "createdAt",
      headerName: "Created At",
      minWidth: 20,
      flex: 1,
      headerStyle: { backgroundColor: "#e9f5ff" },
      filter: false,
      cellStyle: {
        padding: "10px 5px 0 10px",
        textAlign: "left",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      },
      valueGetter: (params: any) => {
        return params.data?.createdAt
          ? new Date(params.data.createdAt).toLocaleDateString("en-US")
          : "NA";
      },
    },
    {
      field: "action",
      headerName: "Action",
      cellStyle: { padding: "10px 15px 0 0", textAlign: "left" },
      headerStyle: { backgroundColor: "#e9f5ff" },
      flex: 0,
      minWidth: 40,
      maxWidth: 90,
      cellRenderer: (params: any) => (
        <div className="flex gap-x-3">
          <button
            onClick={() => handleEdit(params.data)}
            className="p-1 hover:gray-100 rounded"
            title="Edit Round Type"
          >
            <img className="iconTd w-5 h-5" src={images.editIcon} alt="edit" />
          </button>
          <button
            onClick={() => handleDelete(params.data._id)}
            className="p-1 hover:bg-red-100 rounded"
            title="Delete Round Type"
          >
            <Trash2 className="text-red-500" size={23} />
          </button>
        </div>
      ),
    },
  ]);

  const defaultColDef = useMemo(() => {
    return {
      editable: false,
      filter: false,
      tooltipComponentParams: {
        color: "#ececec",
      },
    };
  }, []);

  return (
    <>
      <div className="px-2 py-4 mt-14 min-h-screen">
        <div className="flex justify-between items-center mb-4">
          <h1 className="ml-2">Round Type List</h1>
          <button
            onClick={handleOpenModal}
            className="flex items-center justify-center font-semibold gap-2 bg-[#0fa9a6] text-white px-3 py-2 rounded-full"
          >
            <Plus size={20} /> Add Round Type
          </button>
        </div>

        <div className="tableBlock w-full">
          <div className="card">
            <div
              className="ag-theme-alpine w-full min-h-[400px] border-0 rounded-lg shadow-lg"
              style={
                {
                  "--ag-background-color": "#fff",
                  "--ag-foreground-color": "#2a2a2a",
                  "--ag-header-foreground-color": "#2a2a2a",
                  "--ag-header-background-color": "#E9F5FF",
                  "--ag-odd-row-background-color": "rgba(0, 0, 0, 0.03)",
                  "--ag-header-column-resize-handle-color": "rgb(126, 46, 132)",
                  "--ag-font-size": "0.75rem",
                  "--ag-header-font-weight": "bold",
                } as React.CSSProperties
              }
            >
              <AgGridReact
                rowData={rows}
                columnDefs={columnDefs}
                headerHeight={65}
                rowHeight={50}
                defaultColDef={defaultColDef}
                domLayout="autoHeight"
                pagination={rows.length > 10}
                paginationPageSize={10}
                paginationPageSizeSelector={[10, 20, 50]}
              />
            </div>
          </div>
        </div>
      </div>
      <Modal
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false);
          setEdited(null);
          setFormData({ roundType: "" });
        }}
        closeButton={false}
        modalClass={"bg-white w-full max-w-xl rounded-xl relative"}
      >
        <div className="p-6">
          <h2 className="text-xl font-semibold text-gray-800 border-b pb-2">
            {edited ? "Edit Round Type" : "Add Round Type"}
          </h2>
          <form id="roundType" onSubmit={handleSubmit}>
            <div className="pr-5 mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Round Type <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="roundType"
                value={formData.roundType}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#375998]"
                placeholder="Enter Round Type"
                required
              />
            </div>
          </form>
        </div>
        <div className="flex justify-end gap-4 mt-3 p-2 bg-[#5A7192] w-full rounded-b-lg">
          <button
            onClick={() => {
              setIsOpen(false);
              setEdited(null);
              setFormData({ roundType: "" });
            }}
            className="bg-[#8497B0] font-semibold text-white px-4 py-2 rounded-full hover:opacity-90"
          >
            CANCEL
          </button>
          <button
            type="submit"
            form="roundType"
            className="bg-[#00A8A8] font-semibold text-white px-4 py-2 rounded-full hover:opacity-90"
          >
            SUBMIT
          </button>
        </div>
      </Modal>
    </>
  );
};

export default RoundTypeList;
